import React, { useContext, useEffect, useMemo, useState } from 'react';

import { EditorContext, RicosContext } from './../../../ricos-context';
import type { RicosPluginAddButton, RicosPluginAddButtons } from './../../../ricos-plugins';
import { TOOLBARS } from './../../../editor-common';

import type { OverflownItemsPosition } from '../../types';
import ToggleButton from '../buttons/ToggleButton/ToggleButton';
import { Toolbar } from '../Toolbar/Toolbar';
import styles from './insert-plugin-toolbar-scss';

interface Props {
  buttons: RicosPluginAddButtons;
  onlyShownButtons?: string[];
  type: 'floating' | 'static' | 'regular';
  referenceElement?: React.RefObject<HTMLElement>;
  onButtonClick: (button: RicosPluginAddButton, e: Event) => void;
  overflownItemsPosition?: OverflownItemsPosition;
  options?: { disabled: boolean };
  alignment?: string;
}

const InsertPluginToolbar: React.FC<Props> = ({
  buttons,
  type,
  onButtonClick,
  overflownItemsPosition,
  options,
  alignment,
  onlyShownButtons,
}) => {
  const { adapter } = useContext(EditorContext);
  const { isMobile } = useContext(RicosContext);
  const [wrapper, setWrapper] = useState<null | HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState(adapter.tiptapEditor.view.dom.clientWidth);

  useEffect(() => {
    if (!wrapper || !window?.ResizeObserver) {
      return;
    }

    setMaxWidth(adapter.tiptapEditor.view.dom.clientWidth);

    const resizeObserver = new ResizeObserver(() => {
      setMaxWidth(adapter.tiptapEditor.view.dom.clientWidth);
    });

    resizeObserver.observe(wrapper);

    return () => resizeObserver.disconnect();
  }, [buttons, options, isMobile, wrapper]);

  const renderers = useMemo(
    () =>
      buttons.asArray().reduce((result, addButton) => {
        const button = addButton.getButton();
        return {
          ...result,
          [button.id]: (toolbarItem: $TSFixMe) => (
            <ToggleButton
              toolbarItem={toolbarItem}
              onClick={(e: $TSFixMe) => onButtonClick(addButton, e)}
            />
          ),
        };
      }, {}),
    [buttons]
  );

  const config = useMemo(
    () => buttons.toToolbarButtonsConfig(TOOLBARS.INSERT_PLUGIN, isMobile, options),
    [isMobile, options, buttons]
  );

  return (
    <div className={styles.wrapper} ref={setWrapper}>
      <Toolbar
        type={type}
        maxWidth={maxWidth}
        toolbarItemsConfig={config}
        onlyShownButtons={onlyShownButtons}
        toolbarItemsRenderers={renderers}
        isMobile={false}
        overflownItemsPosition={overflownItemsPosition}
        alignment={alignment}
        isCircularFocusEnabled={false}
      />
    </div>
  );
};

export default InsertPluginToolbar;
